import { skipToken } from '@reduxjs/toolkit/query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';

import {
    isPatientPreferredPharmacyRoute,
    resolveSpecialMemberHandling,
    SPECIAL_MEMBER_HANDLING_FLAG,
    type SpecialMemberHandlingResult,
} from 'services/specialMemberHandling';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';
import { useGetMemberAttributesQuery } from 'store/api/scopeAssessorApi';
import { type ReferralDetails, type TreatmentDetails } from 'store/api/treatmentApi';

type MemberDetailsSource = ReferralDetails | TreatmentDetails;

const isTreatmentDetails = (source: MemberDetailsSource): source is TreatmentDetails =>
    'memberDetails' in source;

const getAttributeLookupArgs = (source?: MemberDetailsSource | null) => {
    if (!source?.payerId) {
        return skipToken;
    }
    if (isTreatmentDetails(source)) {
        const { memberId, firstName, dateOfBirth } = source.memberDetails;
        if (!memberId || !firstName || !dateOfBirth) {
            return skipToken;
        }
        return {
            payerId: source.payerId,
            memberId,
            firstName,
            dateOfBirth,
        };
    }
    if (!source.memberId || !source.memberFirstName || !source.memberDOB) {
        return skipToken;
    }
    return {
        payerId: source.payerId,
        memberId: source.memberId,
        firstName: source.memberFirstName,
        dateOfBirth: source.memberDOB,
    };
};

/**
 * Resolves special-handling tooltip for a referral/treatment member header.
 */
export const useSpecialMemberHandling = (
    source?: MemberDetailsSource | null,
    routing: { routeCode?: string; supplierNpi?: string } = {},
): SpecialMemberHandlingResult | null => {
    const flags = useFlags<Record<string, boolean | undefined>>();
    const showSpecialMemberHandling = !!flags[SPECIAL_MEMBER_HANDLING_FLAG];

    const attributeQueryArgs = useMemo(
        () => (showSpecialMemberHandling ? getAttributeLookupArgs(source) : skipToken),
        [source, showSpecialMemberHandling],
    );

    const { data: memberAttributes = [] } = useGetMemberAttributesQuery(attributeQueryArgs);

    const isPreferredPharmacy = isPatientPreferredPharmacyRoute(routing.routeCode);
    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        showSpecialMemberHandling && isPreferredPharmacy && routing.supplierNpi
            ? { npi: routing.supplierNpi }
            : skipToken,
    );

    return useMemo(
        () =>
            showSpecialMemberHandling
                ? resolveSpecialMemberHandling({
                      attributes: memberAttributes,
                      isPreferredPharmacy,
                      preferredPharmacyName: supplierDetails?.name,
                  })
                : null,
        [showSpecialMemberHandling, memberAttributes, isPreferredPharmacy, supplierDetails?.name],
    );
};
